import React, { useState, useEffect, useRef } from 'react';
import { ORDER_STATUS } from '../orderStatus';
import { api } from '../api';
import SlotChip, { isFlowwowTimed } from '../components/SlotChip';
import { flowwowCustomerName } from '../components/FlowwowOrderPanel';
import { BRAND_NAME } from '../brand';
import OrderModal from '../components/OrderModal';
import DateRangeCalendar from '../components/DateRangeCalendar';
import AdminOrderCreateModal from '../components/AdminOrderCreateModal';
import Tooltip from '../components/Tooltip';
import { formatPhoneDisplay } from '../utils';
import { paymentMethodLabel } from '../utils/paymentMethod';
import { openOrdersStream } from '../utils/ordersStream';

const statusLabels = ORDER_STATUS; // единые подписи (src/orderStatus.js)

const paymentStatusLabels = {
  pending: { label: 'Ожидает', class: 'pending' },
  paid: { label: 'Оплачен', class: 'completed' },
  paid_chat: { label: 'Оплачен (чат)', class: 'completed' },
  failed: { label: 'Ошибка оплаты', class: 'cancelled' },
  expired: { label: 'Истёк срок оплаты', class: 'cancelled' },
  cancelled: { label: 'Отменён', class: 'cancelled' },
  not_required: { label: 'Не требуется', class: 'info' }
};

const PAYMENT_EXPIRE_MINUTES = 60;

function formatDateLabel(from, to) {
  // Короткий формат в шапке: день.месяц без года — экономит место в колонке
  const short = (d) => { const [, m, day] = d.split('-'); return `${day}.${m}`; };
  const full = (d) => { const [y, m, day] = d.split('-'); return `${day}.${m}.${y}`; };
  if (from && to) return `${short(from)} — ${short(to)}`;
  if (from) return `с ${full(from)}`;
  if (to) return `до ${full(to)}`;
  return null;
}

function DateRangeFilter({ label, dateFrom, dateTo, show, onToggle, onSelect, onClear }) {
  const text = formatDateLabel(dateFrom, dateTo);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>{label}</label>
      <div style={{ display: 'flex', gap: 4, position: 'relative' }}>
        <button
          type="button"
          onClick={onToggle}
          style={{
            padding: '6px 10px', fontSize: 13, cursor: 'pointer', textAlign: 'left',
            minWidth: 170, background: '#fff', border: '2px solid #ddd', '--ch': '5px', '--stroke': '#ddd', '--bw': '2px',
            color: text ? '#333' : '#999',
          }}
        >
          {text || 'Выбрать период'}
        </button>
        {text && (
          <button
            type="button"
            onClick={onClear}
            style={{ background: 'none', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', cursor: 'pointer', fontSize: 14, color: '#999', padding: '0 8px' }}
          >✕</button>
        )}
        {show && (
          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, marginTop: 4 }}>
            <DateRangeCalendar
              dateFrom={dateFrom}
              dateTo={dateTo}
              onSelect={onSelect}
              onClose={onToggle}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function OrdersPage({ showToast }) {
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dashboardStats, setDashboardStats] = useState({ today_new: 0, today_paid: 0, completed_total: 0 });
  // Тик каждую минуту для пересчёта countdown'а оплаты
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [orderNumberInput, setOrderNumberInput] = useState('');
  const [orderNumberSearch, setOrderNumberSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchInput, setClientSearchInput] = useState('');

  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');
  const [showOrderCalendar, setShowOrderCalendar] = useState(false);
  const [showDeliveryCalendar, setShowDeliveryCalendar] = useState(false);
  const orderCalendarTriggerRef = useRef(null);
  const deliveryCalendarTriggerRef = useRef(null);
  const [numberSearchOpen, setNumberSearchOpen] = useState(false);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const LIMIT = 100;

  // Полноэкранный спиннер показываем только при первой загрузке.
  // На смене фильтра обновляем «тихо» — шапка таблицы не пропадает.
  const initialLoadedRef = useRef(false);

  useEffect(() => {
    loadOrders(!initialLoadedRef.current);
    initialLoadedRef.current = true;
    loadDashboardStats();

    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream

    const es = openOrdersStream();
    // Пачка событий — один перезапрос. Список заказов тяжёлый, а открыт он обычно на
    // нескольких устройствах: 05.10.2026 под волной заказов каждое событие давало по
    // запросу списка со всех вкладок (118 за 9 минут), и это помогло выбрать пул БД.
    // Случайная добавка разводит устройства по времени.
    let refetchTimer = null;
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_order') {
          if (window.__lastCreatedOrderId && data.order_id === window.__lastCreatedOrderId) return;
          showToast(`Новый заказ #${data.order_number}`);
        } else if (data.type === 'subscription_created') {
          showToast(`Новая подписка #${data.subscription_id}`);
        }
      } catch (e) {}
      if (refetchTimer) return;
      refetchTimer = setTimeout(() => {
        refetchTimer = null;
        loadOrders(false);
        loadDashboardStats();
      }, 2000 + Math.floor(Math.random() * 500));
    };
    return () => { es.close(); if (refetchTimer) clearTimeout(refetchTimer); };
  }, [clientSearch, orderNumberSearch, orderDateFrom, orderDateTo, deliveryDateFrom, deliveryDateTo]);

  const loadDashboardStats = async () => {
    try {
      const data = await api.getOrdersDashboardStats();
      setDashboardStats(data);
    } catch (e) {}
  };

  const loadOrders = async (reset = false) => {
    if (reset) setIsLoading(true);
    try {
      const { total, items } = await api.getOrders({
        search: clientSearch,
        orderId: orderNumberSearch ? parseInt(orderNumberSearch) : null,
        orderDateFrom, orderDateTo,
        deliveryDateFrom, deliveryDateTo,
        limit: LIMIT, offset: 0
      });
      setOrders(items);
      setTotalCount(total);
      setHasMore(items.length === LIMIT);
    } catch (error) {
      showToast('Ошибка загрузки заказов', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setLoadingMore(true);
    try {
      const { items } = await api.getOrders({
        search: clientSearch,
        orderDateFrom, orderDateTo,
        deliveryDateFrom, deliveryDateTo,
        limit: LIMIT, offset: orders.length
      });
      setOrders(prev => [...prev, ...items]);
      setHasMore(items.length === LIMIT);
    } catch (error) {
      showToast('Ошибка загрузки заказов', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleClientSearch = () => setClientSearch(clientSearchInput.trim());

  const handleClientSearchClear = () => {
    setClientSearchInput('');
    setClientSearch('');
  };

  const openOrderModal = (order) => {
    if (window.getSelection()?.toString().length > 0) return;
    setSelectedOrder(order);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return '—';
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };

  const displayedOrders = orders;

  const stats = {
    todayNew: dashboardStats.today_new,
    todayPaid: dashboardStats.today_paid,
    completedTotal: dashboardStats.completed_total
  };

  if (isLoading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title">Заказы</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Создать заказ
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card stat-card-neutral">
          <div className="stat-icon">
            <img src="/icons/nv-stats-new.svg" alt="" />
          </div>
          <div>
            <div className="stat-label">Новых сегодня</div>
            <div className="stat-value">{stats.todayNew}</div>
          </div>
        </div>
        <div className="stat-card stat-card-primary">
          <div className="stat-icon">
            <img src="/icons/nv-stats-paid.svg" alt="" />
          </div>
          <div>
            <div className="stat-label">Оплаченных сегодня</div>
            <div className="stat-value">{stats.todayPaid}</div>
          </div>
        </div>
        <div className="stat-card stat-card-dark">
          <div className="stat-icon">
            <img src="/icons/nv-stats-total.svg" alt="" />
          </div>
          <div>
            <div className="stat-label">Всего выполненных заказов</div>
            <div className="stat-value">{stats.completedTotal}</div>
          </div>
        </div>
      </div>

      {(
        <div className="table-container">
          {/* table-layout: auto — колонки растягиваются по контенту, никогда не уже
             содержимого (нет наезда). Если сумма не влезает в окно — горизонтальный
             скролл на .table-container. min-width у ячеек (whiteSpace:nowrap) держит
             даты/плашки целыми. */}
          <table className="table" style={{ tableLayout: 'auto', minWidth: 1100, width: '100%' }}>
            <thead>
              <tr>
                <th style={{ position: 'relative' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (numberSearchOpen || orderNumberInput) ? 'hidden' : 'visible' }}>
                    <span>№ заказа</span>
                    <button
                      onClick={() => setNumberSearchOpen(true)}
                      title="Поиск по номеру"
                      style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1C3D2E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', '--ch': '5px'}}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(28,61,46,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </div>
                  {(numberSearchOpen || orderNumberInput) && (
                    <div style={{ position: 'absolute', left: 12, right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke="#1C3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}
                      ><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input
                        autoFocus
                        type="text"
                        placeholder="№ заказа"
                        value={orderNumberInput}
                        onChange={(e) => setOrderNumberInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setOrderNumberSearch(e.target.value.trim()); }}
                        onBlur={() => { if (!orderNumberInput) setNumberSearchOpen(false); }}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '6px 26px 6px 30px',
                          border: '1px solid #1C3D2E', '--ch': '6px', '--stroke': '#1C3D2E',
                          fontSize: 13, fontWeight: 500, outline: 'none',
                          background: '#fff', color: '#1C3D2E', textTransform: 'none', letterSpacing: '0.01em',
                        }}
                      />
                      {orderNumberInput && (
                        <button onClick={() => { setOrderNumberInput(''); setOrderNumberSearch(''); setNumberSearchOpen(false); }}
                          style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%',
                            cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >×</button>
                      )}
                    </div>
                  )}
                </th>
                <th>
                  <div
                    ref={orderCalendarTriggerRef}
                    onClick={() => { setShowOrderCalendar(v => !v); setShowDeliveryCalendar(false); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                  >
                    <span>{formatDateLabel(orderDateFrom, orderDateTo) || 'Дата оформления'}</span>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {(orderDateFrom || orderDateTo) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setOrderDateFrom(''); setOrderDateTo(''); }}
                        style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >×</button>
                    )}
                  </div>
                  {showOrderCalendar && (
                    <DateRangeCalendar
                      anchorEl={orderCalendarTriggerRef}
                      dateFrom={orderDateFrom}
                      dateTo={orderDateTo}
                      onSelect={(from, to) => { setOrderDateFrom(from); setOrderDateTo(to); setShowOrderCalendar(false); }}
                      onClose={() => setShowOrderCalendar(false)}
                    />
                  )}
                </th>
                <th style={{ position: 'relative' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (clientSearchOpen || clientSearchInput) ? 'hidden' : 'visible' }}>
                    <span>Клиент</span>
                    <button
                      onClick={() => setClientSearchOpen(true)}
                      title="Поиск по клиенту"
                      style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1C3D2E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', '--ch': '5px'}}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(28,61,46,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </div>
                  {(clientSearchOpen || clientSearchInput) && (
                    <div style={{ position: 'absolute', left: 12, right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke="#1C3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}
                      ><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input
                        autoFocus
                        type="text"
                        placeholder="@username, ID или телефон…"
                        value={clientSearchInput}
                        onChange={(e) => setClientSearchInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleClientSearch(); }}
                        onBlur={() => { if (!clientSearchInput) setClientSearchOpen(false); }}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '6px 26px 6px 30px',
                          border: '1px solid #1C3D2E', '--ch': '6px', '--stroke': '#1C3D2E',
                          fontSize: 13, fontWeight: 500, outline: 'none',
                          background: '#fff', color: '#1C3D2E', textTransform: 'none', letterSpacing: '0.01em',
                        }}
                      />
                      {clientSearchInput && (
                        <button onClick={() => { handleClientSearchClear(); setClientSearchOpen(false); }}
                          style={{
                            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                            width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%',
                            cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >×</button>
                      )}
                    </div>
                  )}
                </th>
                <th>Способ получения</th>
                <th>
                  <div
                    ref={deliveryCalendarTriggerRef}
                    onClick={() => { setShowDeliveryCalendar(v => !v); setShowOrderCalendar(false); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                  >
                    <span>{formatDateLabel(deliveryDateFrom, deliveryDateTo) || 'Дата доставки'}</span>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {(deliveryDateFrom || deliveryDateTo) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeliveryDateFrom(''); setDeliveryDateTo(''); }}
                        style={{ width: 18, height: 18, border: 'none', background: '#1C3D2E', borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >×</button>
                    )}
                  </div>
                  {showDeliveryCalendar && (
                    <DateRangeCalendar
                      anchorEl={deliveryCalendarTriggerRef}
                      dateFrom={deliveryDateFrom}
                      dateTo={deliveryDateTo}
                      onSelect={(from, to) => { setDeliveryDateFrom(from); setDeliveryDateTo(to); setShowDeliveryCalendar(false); }}
                      onClose={() => setShowDeliveryCalendar(false)}
                    />
                  )}
                </th>
                <th>Сумма</th>
                <th>Оплата</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {displayedOrders.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '40px 20px', color: '#5E6561' }}>
                    Заказов не найдено
                  </td>
                </tr>
              )}
              {displayedOrders.map(order => {
                // Админ-заказы (внутренний или с улицы) — клиент это сам админ,
                // показывать платформу не нужно. Для остальных — TG/Max/Web.
                const isAdminCreated = order.admin_order_type === 'internal' || order.admin_order_type === 'walkin';
                const platform = order.source_platform
                  || (order.user?.telegram_id ? 'telegram' : order.user?.max_id ? 'max' : 'web');
                const platformLabel = platform === 'telegram' ? 'TG' : platform === 'max' ? 'Max' : platform === 'flowwow' ? 'Flowwow' : 'Web';
                const platformClass = platform === 'telegram' ? 'platform-tg' : platform === 'max' ? 'platform-max' : platform === 'flowwow' ? 'platform-flowwow' : 'platform-web';
                const platformIcon = platform === 'flowwow' ? '/icons/flowwow.svg'
                  : `/icons/nv-${platform === 'telegram' ? 'tg' : platform === 'max' ? 'max' : 'web'}.svg`;

                // Подпись клиента (username / id / phone)
                const userIdent = order.user?.username
                  ? `@${order.user.username}`
                  : order.user?.telegram_id || order.user?.max_id || (order.user?.phone ? formatPhoneDisplay?.(order.user.phone) || order.user.phone : '');

                const isPickup = order.delivery_type === 'pickup';

                return (
                <tr
                  key={order.id}
                  onClick={() => openOrderModal(order)}
                  onMouseEnter={() => setHoveredRow(order.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{ cursor: 'pointer', backgroundColor: hoveredRow === order.id ? '#F4F4EF' : 'transparent', transition: 'background-color 0.15s' }}
                >
                  {(() => {
                    // Считаем количество чипов в строке. Когда их много (>=5) — применяем
                    // компактный режим, чтобы они не вылезали в соседнюю колонку с датой.
                    const chipsCount = (
                      (order.order_type === 'subscription' ? 1 : 0) +
                      (order.order_type === 'subscription' && order.items_count === 0 ? 1 : 0) +
                      (order.admin_order_type === 'client' ? 1 : 0) +
                      (order.admin_order_type === 'internal' ? 2 : 0) +
                      (order.admin_order_type === 'walkin' ? 2 : 0) +
                      (order.admin_comment ? 1 : 0) +
                      (order.odd_quantity ? 1 : 0) +
                      (order.has_postcard ? 1 : 0) +
                      ((order.history?.length || 0) > 0 ? 1 : 0) +
                      (order.adjustments?.some(a => a.status === 'pending' && a.type === 'surcharge') ? 1 : 0) +
                      (order.adjustments?.some(a => a.status === 'pending' && a.type === 'refund') ? 1 : 0)
                    );
                    // Compact-режим только когда чипов реально много и они не влезают.
                    // Порог 6+ — при 5 чипов нормальный размер ещё помещается рядом с датой.
                    const tdClass = chipsCount >= 6 ? 'chips-compact' : '';
                    return (
                  <td className={tdClass} style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
                    <span style={{ fontWeight: 600 }}>
                      #{order.order_type === 'subscription' ? `${order.subscription_order_number || order.subscription_id}/${order.subscription_delivery_number}` : (order.order_number || order.id)}{order.source_platform === 'flowwow' && order.external_platform_order_id && <span style={{ fontWeight: 500, color: '#C2185B' }}> / {order.external_platform_order_id}</span>}
                    </span>
                    {order.order_type === 'subscription' && <Tooltip text="Подписка"><img src="/icons/nv-stats-subscriptions.svg" alt="Подписка" style={{ width: 16, height: 16, marginLeft: 4 }} /></Tooltip>}
                    {order.order_type === 'subscription' && order.items_count === 0 && <Tooltip text="Ожидает наполнения"><span className="chip chip-empty">пусто</span></Tooltip>}
                    {order.admin_order_type === 'client' && <Tooltip text="Создан админом"><span className="chip chip-admin"><img src="/icons/nv-admin.svg" alt="" />АДМ</span></Tooltip>}
                    {order.admin_order_type === 'internal' && <>
                      <Tooltip text="Создан админом"><span className="chip chip-admin"><img src="/icons/nv-admin.svg" alt="" />АДМ</span></Tooltip>
                      {order.is_marketing
                        ? <Tooltip text="Маркетинговый заказ"><span className="chip chip-marketing">МАРК</span></Tooltip>
                        : <Tooltip text="Внутренний заказ"><span className="chip chip-internal">ВНУТР</span></Tooltip>}
                    </>}
                    {order.admin_order_type === 'walkin' && <>
                      <Tooltip text="Создан админом"><span className="chip chip-admin"><img src="/icons/nv-admin.svg" alt="" />АДМ</span></Tooltip>
                      <Tooltip text={`Трафик (${order.assembly_type === 'bouquet' ? 'букет' : 'охапка'})`}><span className="chip chip-walkin"><img src="/icons/nv-traffic.svg" alt="" />ТРАФИК</span></Tooltip>
                    </>}
                    {order.admin_comment && <Tooltip text={order.admin_comment}><span className="chip-icon chip-comment">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    </span></Tooltip>}
                    {order.odd_quantity && <Tooltip text="Нечётное количество"><span className="chip-icon chip-odd">
                      <img src="/icons/nv-odd.svg" alt="" />
                    </span></Tooltip>}
                    {order.has_postcard && <Tooltip text={order.postcard_price > 0 ? 'Открытка' : 'Открытка (бесплатная, Flowwow)'}><span className="chip-icon chip-postcard">
                      <img src="/icons/mail.svg" alt="" />
                    </span></Tooltip>}
                    {order.history?.length > 0 && <Tooltip text="Заказ редактировался"><span className="chip-icon chip-edited">
                      <img src="/icons/edit.svg" alt="" />
                    </span></Tooltip>}
                    {order.adjustments?.some(a => a.status === 'pending' && a.type === 'surcharge') && <Tooltip text="Ожидает доплата"><span className="chip-icon chip-surcharge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </span></Tooltip>}
                    {order.adjustments?.some(a => a.status === 'pending' && a.type === 'refund') && <Tooltip text="Ожидает возврат"><span className="chip-icon chip-refund">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
                    </span></Tooltip>}
                    </span>
                  </td>
                    );
                  })()}
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{formatDate(order.order_date)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 500 }}>{(() => {
                        if (order.admin_order_type === 'walkin') return order.customer_name || '—';
                        if (order.admin_order_type === 'internal') return order.recipient_name;
                        // У покупателя Flowwow аккаунта нет — имя с площадки или «Клиент»
                        if (platform === 'flowwow') return flowwowCustomerName(order);
                        return `${order.user?.first_name || ''} ${order.user?.last_name || ''}`.trim() || '—';
                      })()}</span>
                      {!isAdminCreated && (
                        <span className={`platform-badge ${platformClass}`}>
                          <img src={platformIcon} alt="" />
                          {platformLabel}
                        </span>
                      )}
                    </div>
                    {!isAdminCreated && userIdent && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{userIdent}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={`delivery-type-badge ${isPickup ? 'delivery-type-pickup' : 'delivery-type-courier'}`}>
                      <img src={`/icons/nv-${isPickup ? 'pickup' : 'delivery'}.svg`} alt="" />
                      {isPickup ? 'Самовывоз' : 'Доставка'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatDeliveryDate(order.delivery_date)}{(order.delivery_time || order.is_asap || isFlowwowTimed(order)) && <>, <SlotChip order={order} /></>}
                  </td>
                  <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {/* Internal с обязательной оплатой → сумма к оплате клиентом.
                        Маркетинговые → реальная сумма заказа (total) для аналитики/учёта. */}
                    {(order.admin_order_type === 'internal' && !order.is_marketing
                      ? (order.client_payment_amount || 0)
                      : (order.total || 0)
                    ).toLocaleString('ru-RU')} ₽
                  </td>
                  <td>
                    <span className={`badge badge-${paymentStatusLabels[order.payment_status]?.class || 'pending'}`}>
                      {(() => {
                        const cls = paymentStatusLabels[order.payment_status]?.class || 'pending';
                        const ps = order.payment_status;
                        const icon = cls === 'paid' || cls === 'paid_chat' ? '/icons/nv-payment-paid.svg'
                          : ps === 'cancelled' ? '/icons/nv-payment-cancelled.svg'
                          : cls === 'pending' ? '/icons/nv-payment-expired.svg'
                          : cls === 'expired' || cls === 'failed' ? '/icons/nv-payment-expired.svg'
                          : cls === 'not_required' ? '/icons/nv-payment-not-required.svg'
                          : null;
                        return icon ? <img src={icon} alt="" /> : null;
                      })()}
                      {(() => {
                        const isPaid = order.payment_status === 'paid' || order.payment_status === 'paid_chat';
                        if (isPaid) {
                          let pm = paymentMethodLabel(order.payment_method);
                          // «Счёт {бренд}» только при 100% бонусной оплате: total=0 + bonus_used>0.
                          // Частичное списание бонусов (total>0) — обычная оплата через ЮКассу/иной метод.
                          if (!pm && (order.total || 0) === 0 && (order.bonus_used || 0) > 0) pm = `Счёт ${BRAND_NAME}`;
                          return pm ? `Оплачен · ${pm}` : 'Оплачен';
                        }
                        if (order.payment_status === 'pending' && order.order_date) {
                          // Для internal-заказов нет дедлайна — просто "Ожидает".
                          if (order.admin_order_type === 'internal') return 'Ожидает';
                          const created = new Date(order.order_date).getTime();
                          const expire = created + PAYMENT_EXPIRE_MINUTES * 60 * 1000;
                          const remainingMs = expire - Date.now();
                          if (remainingMs > 0) {
                            const mins = Math.ceil(remainingMs / 60000);
                            return `Ожидает · ${mins} мин`;
                          }
                          return 'Ожидает · истекает';
                        }
                        return paymentStatusLabels[order.payment_status]?.label || 'Ожидает';
                      })()}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${statusLabels[order.status]?.class || 'os-new'}`}>
                      {(() => {
                        const cls = statusLabels[order.status] ? order.status : 'new';
                        const icon = cls === 'new' ? '/icons/nv-status-new.svg'
                          : cls === 'packaging' ? '/icons/nv-package.svg'
                          : cls === 'ready' ? '/icons/nv-status-ready.svg'
                          : cls === 'courier' ? '/icons/nv-delivery.svg'
                          : cls === 'completed' ? '/icons/nv-status-completed.svg'
                          : cls === 'cancelled' ? '/icons/nv-status-cancelled.svg'
                          : null;
                        return icon ? <img src={icon} alt="" /> : null;
                      })()}
                      {statusLabels[order.status]?.label || order.status}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {displayedOrders.length > 0 && hasMore && !orderNumberSearch && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={loadMoreOrders} disabled={loadingMore}>
            {loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
          </button>
        </div>
      )}

      {showModal && selectedOrder && (
        <OrderModal
          initialOrder={selectedOrder}
          onClose={() => setShowModal(false)}
          showToast={showToast}
          onOrderUpdated={() => { loadOrders(false); }}
        />
      )}

      {showCreateModal && (
        <AdminOrderCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(order) => {
            setShowCreateModal(false);
            // Подавляем SSE-тост для только что созданного заказа
            window.__lastCreatedOrderId = order.id;
            setTimeout(() => { window.__lastCreatedOrderId = null; }, 3000);
            loadOrders(true);
            showToast(`Заказ #${order.order_number || order.id} создан`);
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

export default OrdersPage;
